import { PageSkeleton } from '@cp/ds/src/components/pageSkeleton';
import React from 'react';
import { LoadableComponent } from '@loadable/component';
import { useParams } from 'react-router';
import { RouteConfigComponentProps } from 'react-router-config';
import {
  AppRouteConfig,
  ClientWebRouteParamsContext,
  getRouterContextValue,
  useClientWebResolver,
  useClientWebRouteParams,
} from '@cp/entities/clientWebRoutes';
import { CurrentCityContextProvider } from '@cp/entities/location';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { useConfig } from '@cp/shared/config';
import { useHelmet } from 'src/shared/helmet';
import { CITY_PATH_PARAMETER } from './city';

const RouteMetaTags: React.FC = () => {
  const Helmet = useHelmet();
  const routerContextValue = useClientWebRouteParams();
  const params = useParams<{ [CITY_PATH_PARAMETER]?: string } & Record<string, string>>();
  const resolver = useClientWebResolver();
  const config = useConfig();

  const canonicalHref = React.useMemo(() => {
    const { pageId, pageProps } = routerContextValue;

    if (!pageId || pageId === ClientWebPage.NotFound) {
      return;
    }

    const path = resolver.resolve({ page: pageId, pageProps, pathParams: params });
    return `${config.get('CLIENT_WEB_ORIGIN', '')}${path}`;
  }, [config, params, resolver, routerContextValue]);

  return <Helmet>{canonicalHref ? <link href={canonicalHref} rel="canonical" /> : null}</Helmet>;
};

export const createRouteComponentWrapper = (
  route: AppRouteConfig,
  renderRoute: (routeElement: React.ReactElement) => ReturnType<React.FC>,
) => {
  const Component = (routeComponentProps: RouteConfigComponentProps) => {
    const params = useParams<{ [CITY_PATH_PARAMETER]?: string } & Record<string, string>>();

    const routerContextValue = React.useMemo(
      () =>
        getRouterContextValue({
          routeConfig: route,
          routeMatchParams: params,
        }),
      [params],
    );

    const fallback = <PageSkeleton />;

    const OriginalComponent = route.component as LoadableComponent<RouteConfigComponentProps>;

    if (!OriginalComponent) {
      return null;
    }

    return (
      <ClientWebRouteParamsContext.Provider value={routerContextValue}>
        <CurrentCityContextProvider>
          {renderRoute(<OriginalComponent {...routeComponentProps} fallback={fallback ?? undefined} />)}
          <RouteMetaTags />
        </CurrentCityContextProvider>
      </ClientWebRouteParamsContext.Provider>
    );
  };

  return Component;
};
